import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabase'
import { canAccessPage as canAccessPageFn, canAccessKnowledgeSearch as canAccessKnowledgeSearchFn, knowledgeSearchAccessLevel as knowledgeSearchAccessLevelFn, meetsMinLevel, pageAccessLevel as pageAccessLevelFn } from './permissions'

const AuthContext = createContext({})

/**
 * @param {import('./permissions').RolePagePermissionRow[] | null} rows
 */
function rowsToMap(rows) {
  if (!rows?.length) return new Map()
  return new Map(rows.map((r) => [r.page_key, r]))
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [rolePagePermissions, setRolePagePermissions] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchRolePermissions = useCallback(async (role) => {
    if (role === 'super_admin') {
      setRolePagePermissions(new Map())
      return
    }
    const { data, error } = await supabase
      .from('role_page_permissions')
      .select('page_key, can_access, access_level')
      .eq('role', role)

    if (error) {
      console.error('Error fetching role_page_permissions:', error)
      setRolePagePermissions(new Map())
      return
    }
    setRolePagePermissions(rowsToMap(data || []))
  }, [])

  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()

      if (error) throw error
      setProfile(data)
      await fetchRolePermissions(data?.role ?? 'user')
    } catch (error) {
      console.error('Error fetching profile:', error)
      setProfile(null)
      setRolePagePermissions(new Map())
    } finally {
      setLoading(false)
    }
  }, [fetchRolePermissions])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        fetchProfile(currentUser.id)
      } else {
        setLoading(false)
      }
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        fetchProfile(currentUser.id)
      } else {
        setProfile(null)
        setRolePagePermissions(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [fetchProfile])

  const signIn = useCallback((email, password) => supabase.auth.signInWithPassword({ email, password }), [])

  const signOut = useCallback(() => supabase.auth.signOut(), [])

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    await fetchProfile(user.id)
  }, [user, fetchProfile])

  const isSuperAdmin = profile?.role === 'super_admin'
  const isAdminUser = profile?.role === 'admin' || isSuperAdmin

  const permissionByPage = rolePagePermissions

  const canAccessPage = useCallback(
    (pageKey) => canAccessPageFn(pageKey, permissionByPage, isSuperAdmin),
    [permissionByPage, isSuperAdmin]
  )

  const canAccessKnowledgeSearch = useCallback(
    () => canAccessKnowledgeSearchFn(permissionByPage, isSuperAdmin),
    [permissionByPage, isSuperAdmin]
  )

  const knowledgeSearchAccessLevel = useCallback(
    () => knowledgeSearchAccessLevelFn(permissionByPage, isSuperAdmin),
    [permissionByPage, isSuperAdmin]
  )

  const canOpenSettings =
    isSuperAdmin ||
    canAccessPageFn('settings-users', permissionByPage, false) ||
    canAccessPageFn('settings-integrations', permissionByPage, false)

  const pageAccessLevel = useCallback(
    (pageKey) => pageAccessLevelFn(pageKey, permissionByPage, isSuperAdmin),
    [permissionByPage, isSuperAdmin]
  )

  const canWritePage = useCallback(
    (pageKey) => meetsMinLevel('write', pageAccessLevelFn(pageKey, permissionByPage, isSuperAdmin)),
    [permissionByPage, isSuperAdmin]
  )

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      signIn,
      signOut,
      refreshProfile,
      rolePagePermissions: permissionByPage,
      isSuperAdmin,
      isAdminUser,
      canOpenSettings,
      canAccessPage,
      canAccessKnowledgeSearch,
      knowledgeSearchAccessLevel,
      pageAccessLevel,
      canWritePage,
      /** @deprecated use isSuperAdmin or canOpenSettings */
      isAdmin: isSuperAdmin,
      /** Integrations settings: write only for super_admin (matrix gives admin read on page). */
      canManageIntegrationsWrite: isSuperAdmin,
    }),
    [
      user,
      profile,
      loading,
      signIn,
      signOut,
      refreshProfile,
      permissionByPage,
      isSuperAdmin,
      isAdminUser,
      canOpenSettings,
      canAccessPage,
      canAccessKnowledgeSearch,
      knowledgeSearchAccessLevel,
      pageAccessLevel,
      canWritePage,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}
